import React, { useCallback, useEffect, useState } from 'react';
import { api, type BookkeepingAccounts, type BookkeepingDraft, type BookkeepingLogEntry, type BookkeepingPreview, type BookkeepingStatus, type FortnoxVoucher } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card, ErrorState, Field, Input, Loader, Switch } from '../components/ui';

// AD1 · Admin → Bokföring: pengahändelserna (API: src/bookkeeping/) som dagliga verifikationer i
// Fortnox, en per dag och kategori. Servern kontrollerar admin och att en händelse bara bokförs en
// gång — den här vyn bara visar, förhandsvisar och frågar.

/** Sätts av App.tsx när Fortnox skickar tillbaka hit (?fortnox=ok|denied|failed|invalid_state). */
export const FORTNOX_RESULT_KEY = 'qvenari_fortnox_result';

const kr = (n: number) => n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '–');
const CATEGORY: Record<string, string> = { in: 'Dragningar', transfer: 'Överföringar till markägare', refund: 'Återbetalningar', release: 'Släppt reservation' };
const KIND: Record<string, string> = { ad_purchase: 'Annonsköp', hunt_charge: 'Fällavgift', lodging_booking: 'Boende', membership: 'Medlemsavgift', hunt_booking: 'Jaktbokning' };
const RETURN: Record<string, string> = {
  ok: 'Fortnox är kopplat.', denied: 'Kopplingen avbröts i Fortnox.', failed: 'Kopplingen misslyckades. Försök igen.', invalid_state: 'Länken hade gått ut. Försök igen.',
};

const ACCOUNTS: { key: keyof BookkeepingAccounts; label: string; why: string }[] = [
  { key: 'stripeBalance', label: 'Stripe-saldo', why: '1580 Fordran på kreditkortsföretag. Hela beloppet dras på Qvenaris Stripe-konto (destination charge) och förs vidare till markägaren.' },
  { key: 'ownerLiability', label: 'Skuld till markägare', why: '2899 Övriga kortfristiga skulder. Markägarens del är inte Qvenaris intäkt; skulden nollas av Stripes överföring samma dag.' },
  { key: 'platformFeeRevenue', label: 'Intäkt plattformsavgift', why: '3041 Försäljning tjänst inom Sverige, 25 % moms. Inte 3002/3003 (12 %/6 %).' },
  { key: 'outputVat', label: 'Utgående moms', why: '2611 Utgående moms försäljning inom Sverige, 25 %.' },
];

function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

function readReturn(): string | null {
  try { const r = sessionStorage.getItem(FORTNOX_RESULT_KEY); if (r) sessionStorage.removeItem(FORTNOX_RESULT_KEY); return r; } catch { return null; }
}

function Rows({ rows }: { rows: BookkeepingDraft['rows'] }) {
  const T = useTheme();
  const cell: React.CSSProperties = { padding: '4px 8px', fontVariantNumeric: 'tabular-nums' };
  const debit = rows.reduce((s, r) => s + r.debit, 0);
  const credit = rows.reduce((s, r) => s + r.credit, 0);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...TYPE.small, color: T.text }}>
        <thead><tr style={{ color: T.textMuted, textAlign: 'left' }}><th style={cell}>Konto</th><th style={cell}>Text</th><th style={{ ...cell, textAlign: 'right' }}>Debet</th><th style={{ ...cell, textAlign: 'right' }}>Kredit</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
              <td style={cell}>{r.account}</td><td style={cell}>{r.description}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{r.debit ? kr(r.debit) : ''}</td><td style={{ ...cell, textAlign: 'right' }}>{r.credit ? kr(r.credit) : ''}</td>
            </tr>
          ))}
          <tr style={{ borderTop: `1px solid ${T.border}`, fontWeight: 700 }}>
            <td style={cell} /><td style={cell}>Summa</td><td style={{ ...cell, textAlign: 'right' }}>{kr(debit)}</td><td style={{ ...cell, textAlign: 'right' }}>{kr(credit)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function VoucherStatus({ v }: { v: FortnoxVoucher }) {
  if (v.status === 'booked') return <Badge tone="ok">{v.fortnoxSeries}{v.fortnoxNumber}</Badge>;
  if (v.status === 'error') return <Badge tone="danger">Fel</Badge>;
  return <Badge tone="info">Pågår</Badge>;
}

export function BookkeepingPanel() {
  const T = useTheme();
  const [status, setStatus] = useState<BookkeepingStatus | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<string | null>(() => { const r = readReturn(); return r ? RETURN[r] ?? r : null; });
  const [busy, setBusy] = useState<string | null>(null);
  const [series, setSeries] = useState('');
  const [accounts, setAccounts] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<string[] | null>(null);
  const [from, setFrom] = useState(() => addDays(today(), -1));
  const [to, setTo] = useState(() => addDays(today(), -1));
  const [preview, setPreview] = useState<BookkeepingPreview | null>(null);
  const [showEvents, setShowEvents] = useState(false);
  const [log, setLog] = useState<BookkeepingLogEntry[] | null>(null);

  const applyStatus = (s: BookkeepingStatus) => {
    setStatus(s);
    setSeries(s.voucherSeries);
    setAccounts(Object.fromEntries(ACCOUNTS.map(a => [a.key, String(s.accounts[a.key])])));
  };
  const loadLog = useCallback(() => { api.bookkeepingLog(50).then(setLog).catch(() => setLog([])); }, []);
  useEffect(() => {
    api.bookkeepingStatus().then(s => {
      applyStatus(s);
      if (s.firstUnbookedDay && s.firstUnbookedDay < addDays(today(), -1)) setFrom(s.firstUnbookedDay);
    }).catch(e => setError(e.message || String(e)));
    loadLog();
  }, [loadLog]);

  async function act<R>(key: string, fn: () => Promise<R>): Promise<R | undefined> {
    setBusy(key);
    try { return await fn(); } catch (e: any) { setNotice(`Fel: ${e.message || String(e)}`); return undefined; } finally { setBusy(null); }
  }

  const connect = () => act('connect', async () => { window.location.href = await api.fortnoxConnectUrl(); });
  const disconnect = () => {
    if (!confirm('Koppla bort Fortnox? Tokens tas bort; inget i Fortnox ändras. Du kan koppla igen när som helst.')) return;
    void act('disconnect', async () => { applyStatus(await api.disconnectFortnox()); setNotice('Fortnox är bortkopplat.'); });
  };

  const draftAccounts = (): BookkeepingAccounts | null => {
    const out = {} as BookkeepingAccounts;
    for (const a of ACCOUNTS) { const n = Number(accounts[a.key]); if (!Number.isInteger(n) || n < 1000 || n > 9999) return null; out[a.key] = n; }
    return out;
  };
  const save = (autoBook?: boolean) => {
    const plan = draftAccounts();
    if (!plan) { setNotice('Fel: varje konto ska vara fyra siffror.'); return; }
    void act('save', async () => { applyStatus(await api.updateBookkeepingSettings(series, plan, autoBook)); setNotice('Inställningarna är sparade.'); setProblems(null); });
  };
  const check = () => act('check', async () => setProblems(await api.bookkeepingCheck()));
  const backfill = () => act('backfill', async () => {
    const n = await api.backfillLedger();
    setNotice(n ? `${n} händelser lästes in ur befintliga betalningar. Datumen är uppskattade — granska dem innan du bokför.` : 'Inga nya händelser att läsa in.');
    applyStatus(await api.bookkeepingStatus());
    if (preview) setPreview(await api.bookkeepingPreview(from, to));
  });
  const showPreview = () => act('preview', async () => setPreview(await api.bookkeepingPreview(from, to)));

  const book = () => {
    if (!preview?.drafts.length) return;
    const total = preview.drafts.reduce((s, d) => s + d.rows.reduce((t, r) => t + r.debit, 0), 0);
    const backfilled = preview.backfilledCount ? `\n\n${preview.backfilledCount} av händelserna är inlästa i efterhand med uppskattat datum.` : '';
    if (!confirm(`Bokför ${preview.drafts.length} verifikationer (${from === to ? from : `${from} – ${to}`}, totalt ${kr(total)} kr) i serie ${status?.voucherSeries} i Fortnox?${backfilled}\n\nEn verifikation i Fortnox går inte att ta bort, bara att makulera där.`)) return;
    void act('book', async () => {
      const res = await api.bookPeriod(from, to);
      const ok = res.filter(v => v.status === 'booked').length;
      setNotice(`${ok} av ${res.length} verifikationer bokförda${res.length > ok ? ' — se felen nedan' : ''}.`);
      setPreview(await api.bookkeepingPreview(from, to));
      applyStatus(await api.bookkeepingStatus());
      loadLog();
    });
  };
  const retry = (v: FortnoxVoucher) => act(`retry-${v.id}`, async () => {
    const r = await api.retryFortnoxVoucher(v.id);
    setNotice(r.status === 'booked' ? `Bokförd som ${r.fortnoxSeries}${r.fortnoxNumber}.` : `Fel igen: ${r.error ?? 'okänt'}`);
    setPreview(await api.bookkeepingPreview(from, to));
    loadLog();
  });

  if (error) return <ErrorState message={error} />;
  if (!status) return <Loader text="Hämtar bokföringen…" />;
  const canBook = status.configured && status.connected;
  const h2: React.CSSProperties = { color: T.text, fontSize: 15, fontWeight: 700, margin: '24px 0 10px' };

  return (
    <div>
      <p style={{ ...TYPE.small, color: T.textSub, margin: '0 0 14px' }}>
        Varje dragning, överföring till markägare och återbetalning blir en händelse. Händelserna bokförs i Fortnox som en verifikation per dag och kategori.
        En händelse bokförs bara en gång. Kommer en händelse in efter att dagen bokförts blir den ett tillägg.
      </p>
      {notice && (
        <Card padding={12} style={{ marginBottom: 12, background: notice.startsWith('Fel') ? T.dangerSoft : undefined }}>
          <span role="status" style={{ ...TYPE.small, color: notice.startsWith('Fel') ? T.danger : T.text }}>{notice}</span>
        </Card>
      )}

      <Card padding={16} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: T.text, fontWeight: 700, flex: '1 1 220px' }}>Fortnox{status.companyName ? ` · ${status.companyName}` : ''}</span>
          {!status.configured ? <Badge tone="danger">Inte konfigurerat</Badge> : status.connected ? <Badge tone="ok" dot>Kopplat</Badge> : <Badge>Inte kopplat</Badge>}
          {status.configured && (status.connected
            ? <Button size="sm" variant="secondary" loading={busy === 'disconnect'} disabled={!!busy} onClick={disconnect}>Koppla bort</Button>
            : <Button size="sm" loading={busy === 'connect'} disabled={!!busy} onClick={() => void connect()}>Koppla Fortnox</Button>)}
        </div>
        {!status.configured && (
          <div style={{ ...TYPE.small, color: T.textSub, marginTop: 8 }}>
            Servern saknar Fortnox-appens uppgifter (hemligheterna qvenari-fortnox-client-id och qvenari-fortnox-client-secret). Redirect-URI:n på integrationen ska vara
            https://api.qvenari.com/api/admin/fortnox/callback.
          </div>
        )}
        {status.connected && status.updatedBy && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 6 }}>Senast ändrat av {status.updatedBy}, {fmt(status.updatedAt)}</div>}
      </Card>

      <Card padding={16} style={{ marginBottom: 12 }}>
        <div style={{ color: T.text, fontWeight: 700, marginBottom: 6 }}>Det här bokförs, och det här bokförs inte</div>
        <ul style={{ ...TYPE.small, color: T.textSub, margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
          <li>Annonsköp, fällavgifter och boende dras på Qvenaris Stripe-konto och förs direkt över till markägarens Stripe-konto. Bara plattformsavgiften är Qvenaris intäkt (25 % moms). Markägarens del bokförs som skuld och nollas av överföringen.</li>
          <li>Stripes egna avgifter och utbetalningarna från Stripe till banken bokförs inte här. De bokförs från Stripes avgiftsfaktura och kontoutdraget.</li>
          <li>Släppta kortreservationer visas i händelserna, men inga pengar har rört sig, så de bokförs inte.</li>
          <li>Äldre medlemsavgifter (utan Stripe Connect) ligger kvar som skuld till markägaren på 2899 tills de betalas ut. Utbetalningen bokförs för hand.</li>
          <li>En återbetalning i Stripe ska göras med "återför överföring" och "återbetala plattformsavgift". Annars står markägarens del kvar som fordran på 2899.</li>
        </ul>
      </Card>

      <h2 style={h2}>Serie och konton</h2>
      <Card padding={16}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Verifikationsserie" hint="Serien måste finnas i Fortnox: Inställningar → Bokföring → Verifikationsserier. Förslag: V (Qvenari).">
            <Input value={series} maxLength={3} onChange={e => setSeries(e.target.value.toUpperCase())} style={{ width: 96 }} />
          </Field>
          {ACCOUNTS.map(a => (
            <Field key={a.key} label={a.label} hint={`${a.why}${status.defaultAccounts[a.key] !== Number(accounts[a.key]) ? ` Förslag: ${status.defaultAccounts[a.key]}.` : ''}`}>
              <Input type="number" inputMode="numeric" value={accounts[a.key] ?? ''} onChange={e => setAccounts(m => ({ ...m, [a.key]: e.target.value }))} style={{ width: 110 }} />
            </Field>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
          <Button size="sm" loading={busy === 'save'} disabled={!!busy} onClick={() => save()}>Spara</Button>
          <Button size="sm" variant="secondary" loading={busy === 'check'} disabled={!!busy || !canBook} onClick={() => void check()}>Kontrollera mot Fortnox</Button>
          <span style={{ flex: '1 1 0' }} />
          <Switch label="Bokför gårdagen automatiskt varje natt (kl. 03)" checked={status.autoBookEnabled} disabled={!!busy || !canBook}
            onChange={v => { if (!v || confirm('Slå på nattlig bokföring? Varje natt bokförs gårdagens händelser i Fortnox utan förhandsvisning. Äldre obokförda dagar bokförs inte automatiskt.')) save(v); }} />
        </div>
        {status.autoBookLastDay && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 6 }}>Nattjobbet har bokfört till och med {status.autoBookLastDay}.</div>}
        {problems && (
          <div role="status" style={{ ...TYPE.small, marginTop: 10, color: problems.length ? T.danger : T.text }}>
            {problems.length ? problems.map(p => <div key={p}>{p}</div>) : 'Serien och kontona finns i Fortnox. Klart att bokföra.'}
          </div>
        )}
      </Card>

      <h2 style={h2}>Förhandsvisa och bokför</h2>
      <Card padding={16}>
        <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 10 }}>
          {status.unbookedEvents} händelser är inte bokförda{status.firstUnbookedDay ? ` (från ${status.firstUnbookedDay})` : ''}
          {status.unbookedBackfilled ? `, varav ${status.unbookedBackfilled} inlästa i efterhand med uppskattat datum` : ''}.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Från"><Input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} /></Field>
          <Field label="Till"><Input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} /></Field>
          <Button size="sm" variant="secondary" loading={busy === 'preview'} disabled={!!busy || !from || !to || from > to} onClick={() => void showPreview()}>Förhandsvisa</Button>
          <Button size="sm" variant="secondary" loading={busy === 'backfill'} disabled={!!busy} onClick={() => void backfill()}>Läs in historik</Button>
        </div>

        {preview && (
          <div style={{ marginTop: 16 }}>
            {preview.drafts.length === 0 ? (
              <div style={{ ...TYPE.small, color: T.textMuted }}>Inget att bokföra i perioden.</div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {preview.drafts.map(d => (
                  <div key={`${d.day}-${d.category}`} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ color: T.text, fontWeight: 700, flex: '1 1 200px' }}>{d.day} · {CATEGORY[d.category] ?? d.category}</span>
                      <span style={{ ...TYPE.small, color: T.textMuted }}>{d.eventCount} händelser</span>
                      {!d.balanced && <Badge tone="danger">Balanserar inte</Badge>}
                    </div>
                    <Rows rows={d.rows} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button loading={busy === 'book'} disabled={!!busy || !canBook || preview.drafts.some(d => !d.balanced)} onClick={book}>Bokför {preview.drafts.length} verifikationer</Button>
                  {!canBook && <span style={{ ...TYPE.small, color: T.textMuted }}>Koppla Fortnox först.</span>}
                </div>
              </div>
            )}

            {preview.vouchers.length > 0 && (
              <>
                <div style={{ color: T.text, fontWeight: 700, margin: '18px 0 8px' }}>Redan bokfört i perioden</div>
                {preview.vouchers.map(v => (
                  <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${T.border}` }}>
                    <span style={{ color: T.text, flex: '1 1 220px', minWidth: 0 }}>{v.description}</span>
                    <span style={{ ...TYPE.small, color: T.textMuted }}>{kr(v.rows.reduce((s, r) => s + r.debit, 0))} kr</span>
                    <VoucherStatus v={v} />
                    {v.status === 'error' && <Button size="sm" variant="secondary" loading={busy === `retry-${v.id}`} disabled={!!busy || !canBook} onClick={() => void retry(v)}>Försök igen</Button>}
                    {v.error && <div style={{ ...TYPE.small, color: T.danger, flexBasis: '100%' }}>{v.error}</div>}
                  </div>
                ))}
              </>
            )}

            <div style={{ marginTop: 16 }}>
              <Button size="sm" variant="secondary" onClick={() => setShowEvents(s => !s)}>{showEvents ? 'Dölj' : 'Visa'} händelserna ({preview.events.length})</Button>
              {showEvents && (
                <div style={{ overflowX: 'auto', marginTop: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', ...TYPE.small, color: T.text }}>
                    <thead><tr style={{ color: T.textMuted, textAlign: 'left' }}>
                      {['Dag', 'Typ', 'Mark', 'Text', 'Belopp', 'Avgift', 'Moms', 'Markägare', ''].map(h => <th key={h} style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {preview.events.map(e => (
                        <tr key={e.id} style={{ borderTop: `1px solid ${T.border}`, color: e.category === 'release' ? T.textMuted : T.text }}>
                          <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{e.day}</td>
                          <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{KIND[e.kind] ?? e.kind} · {CATEGORY[e.category] ?? e.category}</td>
                          <td style={{ padding: '4px 6px' }}>{e.markName ?? '–'}</td>
                          <td style={{ padding: '4px 6px' }}>{e.description}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{kr(e.category === 'transfer' ? e.cash : e.gross)}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{e.fee ? kr(e.fee) : ''}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{e.vat ? kr(e.vat) : ''}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{e.owner ? kr(e.owner) : ''}</td>
                          <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                            {e.booked ? <Badge tone="ok">Bokförd</Badge> : e.category === 'release' ? <Badge>Bokförs inte</Badge> : null}
                            {e.backfilled && <Badge tone="info" style={{ marginLeft: 4 }}>Uppskattat datum</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      <h2 style={h2}>Bokföringslogg</h2>
      {log === null ? <Loader text="Hämtar loggen…" /> : log.length === 0 ? (
        <div style={{ ...TYPE.small, color: T.textMuted }}>Inget bokfört än.</div>
      ) : (
        <Card padding={0}>
          {log.map((l, i) => (
            <div key={l.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline', padding: '10px 14px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span style={{ ...TYPE.small, color: T.textMuted, minWidth: 110 }}>{fmt(l.createdAt)}</span>
              <span style={{ color: T.text, fontWeight: 600, flex: '1 1 180px', minWidth: 0 }}>{l.day} · {CATEGORY[l.category] ?? l.category}</span>
              <span style={{ ...TYPE.small, color: l.status === 'booked' ? T.text : T.danger }}>
                {l.status === 'booked' ? `Bokförd ${l.message ?? ''}` : 'Fel'}{l.actor ? ` · ${l.actor}` : ''}
              </span>
              {l.status !== 'booked' && l.message && <div style={{ ...TYPE.small, color: T.danger, flexBasis: '100%' }}>{l.message}</div>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
